<template>
  <div class="container dashboard">
    <dashboard-banner />
    <loader v-if="loadingSurvey" />
    <template v-else>
      <loader v-if="loadingRecommendations" />
      <recommendations
        v-else
        :recommendations="recommendations"
        :schedule-with-counselor="scheduleWithCounselor"
        :recommended-actions="recommendedActions"
        :selected-recommendation="selectedRecommendation"
        :has-appointment-booked="hasAppointmentBooked"
        :user-has-partner-college="userHasPartnerCollege"
        :number-of-free-sessions="$store.getters.currentUser.student_profile.credits_remaining.individual_session_credits_remaining"
        :selected-recommendation-name="selectedRecommendationName"
        :select-recommendation="selectRecommendation"
        style="margin-bottom: 30px;"
      />

      <div
        v-if="hasAppointmentBooked"
        class="row"
      >
        <div class="col-12">
          <div class="welcome-card">
            <div class="row">
              <div class="col-12 col-lg-4 order-lg-2 upper">
                <img
                  class="welcome__img"
                  src="@/assets/img/welcome@2x.png"
                  alt="BetterMynd"
                >
              </div>
              <div class="col-12 col-lg-8 order-lg-1">
                <h1>Welcome back, {{ $store.getters.currentUser.displayName }}!</h1>
                <p>
                  We’re so happy to see you again. It looks like you already have a session
                  scheduled — in the meantime, be sure to check out our blogs and self-help
                  resources.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-lg-4 welcome-sidebar order-2 order-lg-1">
          <div v-if="loadingRecommendations" class="dashboard-card primary">
            <loader />
          </div>
          <div
            v-else-if="primaryCounselor"
            class="dashboard-card primary"
          >
            <div class="left">
              <div
                v-if="primaryCounselor.imageUrl"
                class="user-avatar bg-cover"
                :style="{ backgroundImage: `url(${primaryCounselor.imageUrl})` }"
              />
              <div
                v-else
                class="user-avatar bg-cover"
              />
            </div>
            <div class="right">
              <label>
                Primary Counselor <span class="sr-only">Options</span>
              </label>
              <h2 style="font-size: 23px;">
                <router-link
                  :to="`/student/counselor/profile/${primaryCounselor.id}`"
                  tag="a"
                >
                  {{ primaryCounselor.name }}
                </router-link>
              </h2>
              <ul>
                <li>
                  <button
                    type="button"
                    class="icon-btn"
                    :title="`Schedule a session with ${primaryCounselor.name}`"
                    @click="scheduleWithCounselor(primaryCounselor)"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'calendar-plus']"
                      class="icon alt"
                    />
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    class="icon-btn"
                    :title="`Send a message to ${primaryCounselor.name}`"
                    @click="sendMessage(primaryCounselor)"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'envelope']"
                      class="icon alt"
                    />
                  </button>
                </li>
                <li v-if="primaryCounselor.officePhone">
                  <a
                    class="icon-btn"
                    :href="`tel:${primaryCounselor.officePhone}`"
                    :title="`Call ${primaryCounselor.name}`"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'phone-square']"
                      class="icon alt"
                    />
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="dashboard-card light get-started">
            <h2 class="sr-only">Options</h2>
            <ul>
              <li>
                <button
                  type="button"
                  @click="doScheduleIndividual()"
                >
                  <div class="left">
                    <font-awesome-icon
                      :icon="['fas', 'user-friends']"
                      class="icon alt"
                    />
                  </div>
                  <div class="mid">
                    <strong>INDIVIDUAL SESSIONS</strong>
                    <p>Find a counselor and request an individual session.</p>
                  </div>
                  <div class="right">
                    <font-awesome-icon
                      :icon="['fas', 'caret-right']"
                      class="icon alt"
                    />
                  </div>
                </button>
              </li>
              <li v-if="!isFaculty">
                <button
                  type="button"
                  @click="doScheduleGroup()"
                >
                  <div class="left">
                    <font-awesome-icon
                      :icon="['fas', 'users']"
                      class="icon alt"
                    />
                  </div>
                  <div class="mid">
                    <strong>WELLBEING WORKSHOPS</strong>
                    <p>Find and attend online group workshops.</p>
                  </div>
                  <div class="right">
                    <font-awesome-icon
                      :icon="['fas', 'caret-right']"
                      class="icon alt"
                    />
                  </div>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  @click="viewFreeResources()"
                >
                  <div class="left">
                    <font-awesome-icon
                      :icon="['fas', 'comment-alt']"
                      class="icon alt"
                    />
                  </div>
                  <div class="mid">
                    <strong>FREE RESOURCES</strong>
                    <p>Additional resources you can use anytime on your own.</p>
                  </div>
                  <div class="right">
                    <font-awesome-icon
                      :icon="['fas', 'caret-right']"
                      class="icon alt"
                    />
                  </div>
                </button>
              </li>
            </ul>
          </div>
          <div class="dashboard-card primary">
            <h2
              style="font-size: 28px; margin-bottom: 15px;"
            >
              Credits
            </h2>
            <p
              style="color: rgba(255,255,255,0.67)"
            >
              Credits provided by {{ $store.getters.currentUser.school.name }}
            </p>
            <table
              class="table bm-table"
              style="margin-bottom: 0;"
            >
              <tbody>
                <tr>
                  <td>
                    Individual Sessions Credits:
                  </td>
                  <td>
                    {{ $store.getters.currentUser.student_profile.credits_remaining.individual_session_credits_remaining }}
                  </td>
                </tr>
                <tr v-if="!isFaculty">
                  <td>
                    Wellbeing Workshop Credits:
                  </td>
                  <td>
                    {{ $store.getters.currentUser.student_profile.credits_remaining.group_session_credits_remaining }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <college-dashboard-display :college="college" />
        </div>
        <div class="col-12 col-lg-8 order-1 order-lg-2">
          <div v-if="loadingWorkshops || loadingSessions" class="dashboard-card light" >
            <Loader />
          </div>
          <session-reminder
            v-else
            :sessions="scheduledAppointments"
            :workshops="workshops"
          />

          <div v-if="$store.getters.currentUser.school.messageForNewUserPrompt" class="dashboard-card appt-table">
            <h2>{{ $store.getters.currentUser.school.name }} Credit Policy</h2>
            <span v-html="$store.getters.currentUser.school.messageForNewUserPrompt" />
          </div>

          <div v-if="scheduledAppointments && scheduledAppointments.length" class="dashboard-card appt-table">
            <h2>Upcoming Sessions</h2>

            <loader v-if="loadingSessions" />

            <div v-else-if="scheduledAppointments && scheduledAppointments.length" class="table-responsive">
              <table class="table bm-table stacked">
                <thead>
                  <tr>
                    <th>
                      Counselor Name
                    </th>
                    <th>
                      Date / Time
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(apt) in scheduledAppointments" :key="apt.id">
                    <td data-label="Counselor Name">
                      <router-link
                        :to="`/student/counselor/profile/${apt.counselor.id}`"
                        tag="a"
                      >
                        {{ apt.counselor.name }}
                      </router-link>
                    </td>
                    <td data-label="Date / Time">{{ apt.start_time | dateLongMonthNoYear }}</td>
                    <td data-label="">
                      <button
                        type="button"
                        class="btn btn-primary w-100"
                        @click="cancelApt(apt, true)"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else>
              <div class="no-results">
                <p>You do not have any upcoming sessions.</p>
              </div>
            </div>
          </div>
          <div v-if="pendingAppointments && pendingAppointments.length" class="dashboard-card appt-table">
            <h2>Requested Sessions</h2>
            <loader v-if="loadingSessions" />
            <div v-else-if="pendingAppointments && pendingAppointments.length" class="table-responsive">
              <table class="table bm-table stacked">
                <thead>
                  <tr>
                    <th>
                      Counselor Name
                    </th>
                    <th>Status</th>
                    <th>
                      Date / Time
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="appointment in pendingAppointments"
                    :key="appointment.id"
                  >
                    <td data-label="Counselor Name">
                      <router-link
                        :to="`/student/counselor/profile/${appointment.counselor.id}`"
                        tag="a"
                      >
                        {{ appointment.counselor.name }}
                      </router-link>
                    </td>
                    <td data-label="Status">Pending</td>
                    <td data-label="Date / Time">{{ appointment.start_time | dateLongMonthNoYear }}</td>
                    <td data-label="">
                      <button
                        type="button"
                        class="btn btn-primary w-100"
                        @click="cancelRequest(appointment)"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else>
              <div class="no-results">
                <p>You do not have any outstanding session requests.</p>
              </div>
            </div>
          </div>

          <div v-if="recentAppointments && recentAppointments.length" class="dashboard-card appt-table" >
            <h2>Recent Sessions</h2>
            <loader v-if="loadingSessions" />
            <div v-else-if="recentAppointments && recentAppointments.length" class="table-responsive">
              <table class="table bm-table stacked">
                <thead>
                  <tr>
                    <th>
                      Counselor Name
                    </th>
                    <th>
                      Date / Time
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="appointment in recentAppointments"
                    :key="appointment.id"
                  >
                    <td data-label="Counselor Name">
                      <router-link
                        :to="`/student/counselor/profile/${appointment.counselor.id}`"
                        tag="a"
                      >
                        {{ appointment.counselor.name }}
                      </router-link>
                    </td>
                    <td data-label="Date / Time">{{ appointment.start_time | dateLongMonthNoYear }}</td>
                    <td>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else>
              <div class="no-results">
                <p>You have not had any sessions this past week.</p>
              </div>
            </div>
          </div>

          <div v-if="!isFaculty && workshops && workshops.length" class="dashboard-card events-table">
            <h2>
              <router-link
                to="/workshops"
                tag="a"
              >
                My Wellbeing Workshops
              </router-link>
            </h2>

            <loader v-if="loadingWorkshops" />

            <div v-else-if="!workshops || workshops.length === 0" class="row">
              <div class="col-12">
                <p>You do not have any Wellbeing Workshops yet.</p>
              </div>
            </div>


            <div v-else class="row">
              <div v-for="workshop in workshops" :key="workshop.id" class="col-12 col-lg-4">
                <div class="event">
                  <h4>{{ workshop.topic }}</h4>
                  <span class="date">{{ workshop.start_time | date }}</span>
                  <div class="mt-2">
                    <button
                      type="button"
                      class="btn btn-primary btn-wide"
                      @click="viewSession(workshop)"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'caret-right']"
                        class="icon alt d-none d-sm-inline d-md-none d-xl-inline"
                      />
                      Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <send-message-modal ref="sendMessageModal" />

      <workshop-view-modal
        ref="workshopViewModal"
        @withdraw="withdrawFromWorkshop"
      />

      <confirm-modal
        ref="confirmWithdrawModal"
        title="Withdraw from workshop"
        cancel-text="No"
        @ok="doWithdraw"
      />

      <confirm-modal
        ref="confirmCancelSessionModal"
        title="Cancel Session"
        :confirm-body="confirmBody"
        :on-confirm="doCancel"
        cancel-text="No"
      />
      <confirm-modal
        ref="confirmCancelRequestModal"
        title="Cancel Request"
        :confirm-body="'Are you sure you want to cancel this session request?'"
        :on-confirm="doCancelRequest"
        cancel-text="No"
      />
      <prompts />
    </template>
  </div>
</template>

<script>
import SendMessageModal from '@/components/modals/SendMessageModal.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import Recommendations from '@/students/components/recommendations/Recommendations.vue';
import CollegeDashboardDisplay from '@/components/CollegeDashboardDisplay';
import UserClient from '@/api/UserClient';
import WorkshopClient from '@/api/WorkshopClient';
import StudentClient from '@/api/StudentClient';
import SessionClient from '@/api/SessionClient';
import MeetingsClient from '@/api/MeetingsClient';
import DashboardBanner from './components/banner/DashboardBanner.vue'
import moment from 'moment';
import Prompts from '@/students/prompts/prompts.vue';
import SessionReminder from './components/SessionReminder.vue'
import WorkshopViewModal from '../workshops/components/WorkshopViewModal.vue';
import WorkshopView from '../workshops/WorkshopView';
import SessionRequestModal from '../session-request/SessionRequestModal.vue';
import RecommendedActionModel from '@/models/RecommendedActionModel.js';
import Loader from '@/components/Loader.vue';

export default {
  name: 'StudentDashboard',
  components: {
    CollegeDashboardDisplay,
    SendMessageModal,
    Recommendations,
    ConfirmModal,
    DashboardBanner,
    Prompts,
    SessionReminder,
    WorkshopViewModal,
    Loader,
  },
  data() {
    return {
      saving: false,
      userClient: new UserClient(this.$http),
      workshopClient: new WorkshopClient(this.$http),
      studentClient: new StudentClient(this.$http),
      MeetingsClient: new MeetingsClient(this.$http),
      sessionsClient: new SessionClient(this.$http),

      workshops: [],
      isFaculty: false,

      loadingSurvey: true,
      loadingRecommendations: true,
      loadingCredits: true,
      loadingWorkshops: true,
      loadingSessions: true,

      recommendations: null, // counselors
      recommendedActions: null,
      selectedRecommendation: null,
      primaryCounselor: null,
      college: this.$store.getters.currentUser.school,
      recentAppointments: [],
      pendingAppointments: [],
      scheduledAppointments: [],

      selectedApt: null,
      confirmBody: '',
      selectedPendingApt: false,
    };
  },
  computed: {
    hasAppointmentBooked() {
      return (this.scheduledAppointments && this.scheduledAppointments.length > 0) ||
        (this.pendingAppointments && this.pendingAppointments.length > 0);
    },
    userHasPartnerCollege() {
      return !!this.college;
    },

    selectedRecommendationName() {
      if (this.recommendations && this.selectedRecommendation) {
        return this.selectedRecommendation.name
      }
      return '';
    },
  },
  async mounted() {
    this.loadingSurvey = true;

    if (this.$store.getters.currentUser && this.$store.getters.currentUser.needsWelcome) {
      this.$router.push({ name: this.$store.getters.currentUser.homeRouteName });
      return;
    }

    this.loadingSurvey = false;

    this.isFaculty = await this.$globals.schoolHasFeature(
      'users-are-faculty',
      this.$store.getters.currentUser.school.id);

    await this.loadDashboard();
  },
  methods: {
    viewSession(workshop) {
      this.$refs.workshopViewModal.show(workshop);
    },
    async loadWorkshops() {
      try {
        this.loadingWorkshops = true;
        const filters = {
          pageToken: undefined,
          userId: this.$store.getters.currentUser.id,
          startsAfter: undefined,
          startsBefore: undefined,
          specialtyIds: undefined,
          userIsAttending: true
        }
        const response = await this.workshopClient.listForUser(
          filters.pageToken,
          filters.userId,
          filters.startsAfter,
          filters.startsBefore,
          filters.specialtyIds,
          filters.userIsAttending);
        this.workshops = [];
        for(const workshop of response.workshops) {
          this.workshops.push(new WorkshopView(workshop));
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        this.loadingWorkshops = false;
      }
    },

    async loadDashboard(appointmentsOnly = false) {
      if (appointmentsOnly) {
        await this.loadSessions();
        await this.loadRequests();
      } else {
        await Promise.all([
          this.loadRecommendations(),
          this.loadCredits(),
          this.loadWorkshops(),
          this.loadSessions(),
          this.loadRequests(),
        ]);
      }
    },
    async loadCredits() {
      this.$globals.updateRemainingCredits();
    },
    async loadRecommendations() {
      this.loadingRecommendations = true;
      this.primaryCounselor = null;
      this.recommendations = [];
      this.recommendedActions = new RecommendedActionModel({
        recommended_action: 'free_individual_session',
        recommended_counselor: null,
      });

      try {
        const response = await this.studentClient.getPrimaryCounselor(
          this.$store.getters.currentUser.id
        );
        if (response && response.counselor) {
          this.primaryCounselor = {
            ...response.counselor,
            name: `${response.counselor.firstName} ${response.counselor.lastName}`,
          };
          this.recommendedActions.recommended_counselor = this.primaryCounselor;
          this.recommendations = [this.primaryCounselor];
          this.selectedRecommendation = this.primaryCounselor;
          this.loadingRecommendations = false;
          return;
        }
      } catch (ex) {
        console.error(ex)
      }

      try {
        const response = await this.studentClient.getRecommendedCounselors(
          this.$store.getters.currentUser.id,
          3,
          moment().toISOString()
        );
        if (response && response.counselors && response.counselors.length) {
          this.recommendations = response.counselors.map(counselor => ({
            ...counselor,
            name: `${counselor.firstName} ${counselor.lastName}`
          }));
          this.selectedRecommendation = this.recommendations[0];
          return;
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        this.loadingRecommendations = false;
      }
    },
    async loadRequests() {
      this.loadingRequests = true;
      this.pendingAppointments = [];

      try {
        const response = await this.studentClient.getMyAppointmentRequests();

        this.pendingAppointments = (response && response.items) || [];
      } catch (ex) {
        console.error(ex)
      } finally {
        this.loadingRequests = false;
      }
    },
    async loadSessions() {
      this.loadingSessions = true;
      this.scheduledAppointments = [];
      this.recentAppointments = [];

      try {
        const userId = this.$store.getters.currentUser.id;
        const from = moment().subtract(1, 'week').startOf('day').toISOString();
        const through = undefined;
        const canceled = false;
        const recordCount = 50;
        const pageToken = 'ListSessionsByStartTimeAscending';

        const response = await this.sessionsClient.getSessionsForUser(
          userId,
          from,
          through,
          canceled,
          recordCount,
          pageToken
        );
        const sessions = response.sessions.map(x => ({
          ...x,
          meeting_id: x.meetingId,
          existing_patient: x.counselor.sessionCount > 1,
          start: x.start ? moment(x.start) : null,
          start_time: x.start ? moment(x.start).toDate() : null,
          end: x.end ? moment(x.end) : null,
          end_time: x.end ? moment(x.end).toDate() : null,
          user: {
            ...x.client,
            id: x.client.userId,
            first_name: x.client.firstName,
            last_name: x.client.lastName,
            name: `${x.client.firstName} ${x.client.lastName}`
          },
          counselor: {
            ...x.counselor,
            name: `${x.counselor.firstName} ${x.counselor.lastName}`,
            user: {
              id: x.counselor.userId,
              first_name: x.counselor.firstName,
              last_name: x.counselor.lastName,
              name: `${x.counselor.firstName} ${x.counselor.lastName}`
            }
          }
        }))

        const now = moment();
        for (const session of sessions) {
          if(session.end.isBefore(now))
            this.recentAppointments.push(session);
          else
            this.scheduledAppointments.push(session);
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        this.loadingSessions = false;
      }
    },


    withdrawFromWorkshop(workshop) {
      this.$refs.workshopViewModal.hide();
      const confirmMessage = this.withdrawConfirmMessage(workshop)
      this.$refs.confirmWithdrawModal.show(workshop, confirmMessage);
    },
    withdrawConfirmMessage(workshop) {
      if (!workshop) return 'Are you sure you want to withdraw from this workshop?';

      const start_time = moment(workshop.start_time);
      const now = moment();
      if (start_time.diff(now, 'hours') > 24) {
        return 'Are you sure you want to withdraw from this workshop? You will receive a full refund.';
      }

      return 'Are you sure you want to withdraw from this workshop? You will NOT receive a refund. (Within 24 hours of workshop start)'
    },
    async doWithdraw(workshop) {
      try {
        this.$refs.confirmWithdrawModal.hide();
        await this.workshopClient.withdrawByUser(workshop.id, this.$store.getters.currentUser.id)
        this.$store.commit('setGlobalAlert', {
          msg: 'You have successfully withdrawn from the workshop.',
        });
        await this.loadWorkshops();
      } catch (ex) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to withdraw from workshop. Please try again!', error: true,
        });
      }
    },
    cancelRequest(apt) {
      this.selectedApt = apt;

      this.$refs.confirmCancelRequestModal.show();
    },
    async doCancelRequest() {
      try {
        await this.sessionsClient.cancelRequest(this.selectedApt.id);

        this.$refs.confirmCancelRequestModal.hide();
        this.$store.commit('setGlobalAlert', {
          msg: 'Session Request Canceled.',
        });
        this.loadDashboard(true);
      } catch (ex) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to cancel session request. Please try again!', error: true,
        });
        this.closing = false;
        this.$refs.confirmCancelRequestModal.hide();
      }
    },

    cancelApt(apt) {
      this.selectedApt = apt;

      this.confirmBody = moment(apt.start_time).diff(moment(), 'hours') > 24
        ? 'Are you sure you want to cancel this session? You will receive a full refund.'
        : 'Are you sure you want to cancel this session? You will NOT receive a refund. (Within 24 hours of session start)';

      this.$refs.confirmCancelSessionModal.show();
    },
    async doCancel() {
      try {
        const res = await this.sessionsClient.cancelSessionByUser(this.selectedApt.id);
        this.$refs.confirmCancelSessionModal.hide();
        this.$store.commit('setGlobalAlert', {
          msg: 'Session Canceled.',
        });
        this.loadDashboard(true);
      } catch (ex) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to cancel appointment. Please try again!', error: true,
        });
        this.closing = false;
        this.$refs.confirmCancelSessionModal.hide();
      }
    },
    selectRecommendation(model, goToView = false) {
      const recommendation = {
        id: model.id,
        userId: model.userId,
        name: `${model.firstName} ${model.lastName}`,
      }
      if (goToView || this.$globals.isMobile) {
        this.scheduleWithCounselor(recommendation);
      } else {
        this.selectedRecommendation = recommendation;
      }
    },
    scheduleWithCounselor(counselor) {
      if (this.$store.getters.currentUser.num_pending_payments > 0) {
        return this.$store.commit('setGlobalAlert', {
          msg: 'To schedule a session you must first complete your pending payments.', error: true,
        });
      }
      this.$modal.show(
        SessionRequestModal,
        {
          props: { counselor },
          listeners: {
            onerror: (err) => {
              console.error(err);
            },
            onsendamessage: (counselor) => {
              this.$refs.sendMessageModal.show(counselor.userId);
            },
            onrequest: (requestResult) => {
              this.$gtag.event('appointment_request', {
                student_id: requestResult.user.id,
                counselor_id: requestResult.counselor_id,
                start_time: requestResult.start_time,
              });
              this.$router.push({ name: 'dashboard', params: { from: 'event-scheduled' } });
            }
          }
        });
    },
    sendMessage(counselor) {
      this.$refs.sendMessageModal.show(counselor.userId);
    },

    doScheduleIndividual() {
      this.$router.push({ name: 'counselors' });
    },
    doScheduleGroup() {
      this.$router.push({ name: 'workshops' });
    },
    viewFreeResources() {
      this.$router.push({ name: 'resources' });
    },
  },
};
</script>

<style lang="scss" scoped>
  .fa-btn {
    background: none;
    color: $green;
    text-decoration: underline;
    border: none;
    box-shadow: none;
  }

  @media (max-width: $md-min) {
  .table.bm-table.stacked{
    thead {
      display: none;
    }
    tr {
      display: block;
      margin-bottom: 20px;
      td:first-of-type {
        padding-left: 0.75rem;
      }
    }
    td {
      display: block;
      text-align: right;
      &::before {
        content: attr(data-label);
        float: left;
        font-weight: bold;
      }
    }
  }
}

</style>
